import { ChangeDetectionStrategy, Component, computed, HostListener, input, output } from '@angular/core';
import { DateAdapter } from '../adapters/abstract-date-adapter';
import { HxCalendarBody } from './calendar-body';
import { CalendarCell } from './calendar-cell';

/** Year view component displaying 12 months in a grid. Delegates rendering to CalendarBody. */
@Component({
  selector: 'hx-year-view',
  standalone: true,
  imports: [HxCalendarBody],
  template: `
    <hx-calendar-body
      [activeCell]="activeCellIndex()"
      [cellAspectRatio]="7 / 4"
      [label]="yearLabel()"
      [numCols]="3"
      [rows]="monthRows()"
      [selectedCell]="selectedCellIndex()"
      (activeDateChange)="activeDateChange.emit($event)"
      (selectedChange)="selectedChange.emit($event)" />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxYearView<D = Date> {
  readonly dateAdapter = input.required<DateAdapter<D>>();
  readonly activeDate = input.required<D>();
  readonly selected = input<D | null>(null);
  readonly minDate = input<D | null>(null);
  readonly maxDate = input<D | null>(null);
  readonly allowedYears = input<number[] | null>(null);

  readonly selectedChange = output<D>();
  readonly activeDateChange = output<D>();

  protected readonly yearLabel = computed(() => {
    const adapter = this.dateAdapter();
    return adapter.getYear(this.activeDate()).toString();
  });

  protected readonly monthRows = computed(() => {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    const year = adapter.getYear(active);
    const monthNames = adapter.getMonthNames('short');
    const today = adapter.today();
    const currentYear = adapter.getYear(today);
    const currentMonth = adapter.getMonth(today);

    const rows: CalendarCell<D>[][] = [];
    let currentRow: CalendarCell<D>[] = [];

    for (let month = 0; month < 12; month++) {
      const date = adapter.create(year, month, 1);
      const enabled = this.isMonthEnabled(date);
      const isCurrentMonth = year === currentYear && month === currentMonth;

      currentRow.push({
        value: date,
        displayValue: monthNames[month]!,
        ariaLabel: `${monthNames[month]} ${year}`,
        enabled,
        cssClasses: [...(isCurrentMonth ? ['hx-calendar-body__cell--today'] : []), 'hx-calendar-body__cell--large'],
      });

      if (currentRow.length === 3) {
        rows.push(currentRow);
        currentRow = [];
      }
    }

    return rows;
  });

  protected readonly activeCellIndex = computed(() => {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    return adapter.getMonth(active);
  });

  protected readonly selectedCellIndex = computed(() => {
    const selected = this.selected();
    if (!selected) {
      return null;
    }

    const adapter = this.dateAdapter();
    const activeYear = adapter.getYear(this.activeDate());
    const selectedYear = adapter.getYear(selected);

    if (activeYear !== selectedYear) {
      return null;
    }

    return adapter.getMonth(selected);
  });

  private isMonthEnabled(date: D): boolean {
    const adapter = this.dateAdapter();
    const min = this.minDate();
    const max = this.maxDate();
    const years = this.allowedYears();

    const year = adapter.getYear(date);
    const month = adapter.getMonth(date);

    // Check allowed years first
    if (years && years.length > 0) {
      if (!years.includes(year)) {
        return false;
      }
    }

    if (min) {
      const minYear = adapter.getYear(min);
      const minMonth = adapter.getMonth(min);
      if (year < minYear || (year === minYear && month < minMonth)) {
        return false;
      }
    }

    if (max) {
      const maxYear = adapter.getYear(max);
      const maxMonth = adapter.getMonth(max);
      if (year > maxYear || (year === maxYear && month > maxMonth)) {
        return false;
      }
    }

    return true;
  }

  /** Handle keyboard navigation within the year view. */
  @HostListener('keydown', ['$event'])
  protected handleKeydown(event: KeyboardEvent): void {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    let newDate: D | null = null;

    switch (event.key) {
      // Arrow navigation (4 columns)
      case 'ArrowLeft':
        newDate = adapter.addCalendarMonths(active, -1);
        break;
      case 'ArrowRight':
        newDate = adapter.addCalendarMonths(active, 1);
        break;
      case 'ArrowUp':
        newDate = adapter.addCalendarMonths(active, -4);
        break;
      case 'ArrowDown':
        newDate = adapter.addCalendarMonths(active, 4);
        break;

      // Year boundaries
      case 'Home':
        newDate = adapter.create(adapter.getYear(active), 0, 1);
        break;
      case 'End':
        newDate = adapter.create(adapter.getYear(active), 11, 1);
        break;
      case 'PageUp':
        newDate = adapter.addCalendarYears(active, event.altKey ? -10 : -1);
        break;
      case 'PageDown':
        newDate = adapter.addCalendarYears(active, event.altKey ? 10 : 1);
        break;

      // Selection
      case 'Enter':
      case ' ':
        this.selectedChange.emit(active);
        event.preventDefault();
        return;

      default:
        return;
    }

    if (newDate) {
      const min = this.minDate();
      const max = this.maxDate();

      if (min && adapter.compareDate(newDate, min) < 0) {
        newDate = min;
      }
      if (max && adapter.compareDate(newDate, max) > 0) {
        newDate = max;
      }

      this.activeDateChange.emit(newDate);
      event.preventDefault();
    }
  }
}
